import React, { ReactElement, useMemo, useCallback } from "react";
import { TaskListFormProps } from "./TaskListForm.types";
import { FormSection, FormControl, FormTokenizedObjectInput } from "src/ui/components/Forms";
import { TextField, DatePicker, Checkbox, IDropdownOption, Dropdown } from "@fluentui/react";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import
    CalendarEventRecurringSelector, {
    IRecurringEventConfig
} from "src/calendar/components/CalendarEventRecurringSelector";
import { UnstyledButton } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import ITokenizedInputItem from "src/ui/components/Forms/ITokenizedInputItem";
import { Conditional } from "src/components/Conditional";

const localizationKeys = [
    "Namn",
    "Typ",
    "Installningar",
    "RECURRING",
    "QH_RESET_FREQUENCY",
    "Startdatum",
    "Slutdatum",
    "RiktadTillBeskrivning",
    "ALLOW_CUSTOMIZATION",
] as const;

function memoizeLocalizationKeys(keys: readonly string[]) {
    return keys.reduce((dict, key) => ({ ...dict, [key]: localize(key) }), {});
}

type RecurrenceOptions
    = "NEVER"
    | "DAILY"
    | "WEEKLY"
    | "MONTHLY";

function getRecurrenceOptions(): IDropdownOption<RecurrenceOptions>[] {
    return [
        {
            data: "NEVER",
            key: "NEVER",
            text: localize("Aldrig"),
        },
        {
            data: "DAILY",
            key: "DAILY",
            text: localize("DAILY"),
        },
        {
            data: "WEEKLY",
            key: "WEEKLY",
            text: localize("WEEKLY"),
        },
        {
            data: "MONTHLY",
            key: "MONTHLY",
            text: localize("MONTHLY"),
        },
    ];
}

type TextFieldChangeHandler = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
) => void;

type CheckboxChangeHandler = (
    ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    checked?: boolean
) => void;

function calendarRecurrenceFrequencyToTyped(freq: string): Spintr.RecurrenceType {
    switch (freq) {
    case "DAILY":
        return SpintrTypes.RecurrenceType.Daily;
    case "WEEKLY":
        return SpintrTypes.RecurrenceType.Weekly;
    case "MONTHLY":
        return SpintrTypes.RecurrenceType.Monthly;
    case "YEARLY":
        return SpintrTypes.RecurrenceType.Yearly;
    default:
        return SpintrTypes.RecurrenceType.None;
    }
}

function typedToCalendarRecurrenceFrequency(freq: Spintr.RecurrenceType): string {
    switch (freq) {
    case SpintrTypes.RecurrenceType.Daily:
        return "DAILY";
    case SpintrTypes.RecurrenceType.Weekly:
        return "WEEKLY";
    case SpintrTypes.RecurrenceType.Monthly:
        return "MONTHLY";
    case SpintrTypes.RecurrenceType.Yearly:
        return "YEARLY";
    default:
        return "NEVER";
    }
}

const dayCodes: string[] = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

const targetTypes = [
    SpintrTypes.UberType.User,
    SpintrTypes.UberType.Department,
    SpintrTypes.UberType.Office,
    SpintrTypes.UberType.Company,
    SpintrTypes.UberType.Role,
    SpintrTypes.UberType.TargetGroup,
] as const;


/**
 * Renders a Task List Form component based on the provided props.
 *
 * @param {TaskListFormProps} props - The properties to configure the Task List Form.
 * @returns {ReactElement} The rendered Task List Form component.
 */
function TaskListForm({
    model,
    onModelChanged,
}: TaskListFormProps): ReactElement {
    const l10n = useMemo(() => memoizeLocalizationKeys(localizationKeys), []);

    const onNameChanged = useCallback<TextFieldChangeHandler>(
        (_, newValue) => onModelChanged?.((model) => ({
                ...model,
                name: newValue ?? "",
        })),
        [onModelChanged]);

    const onAllowOverrideChanged = useCallback<CheckboxChangeHandler>(
        (_, checked) => onModelChanged?.((model) => ({
                ...model,
                allowOverride: checked ?? false,
        })),
        [onModelChanged]);

    const recurrenceConfig = useMemo(() => ({
        freq: typedToCalendarRecurrenceFrequency(
            model.recurrencePattern?.type ?? SpintrTypes.RecurrenceType.None),
        interval: model.recurrencePattern?.interval ?? 1,
        byDay: model.recurrencePattern?.daysOfWeek?.map((dayIndex) => dayCodes[dayIndex])?.join(","),
        dtStart: model.startDate,
        until: model.endDate,
        bySetPos: model.recurrencePattern?.day,
        byMonthDate: model.recurrencePattern?.month,
    }), [model.recurrencePattern, model.startDate, model.endDate]);

    const onRecurrencePatternChanged = useCallback(
        (pattern: IRecurringEventConfig) => {console.log(pattern);onModelChanged?.((model) => ({
            ...model,
            startDate: !pattern ? model.startDate : pattern.dtStart,
            endDate: !pattern ? model.endDate : pattern.until,
            recurrencePattern: !pattern
                ? { type: SpintrTypes.RecurrenceType.None, interval: 1 }
                : {
                    type: calendarRecurrenceFrequencyToTyped(pattern.freq),
                    interval: pattern.interval,
                    daysOfWeek: !pattern.byDay
                        ? undefined
                        : pattern.byDay
                            .split(",")
                            .map((dayCode) => dayCodes.indexOf(dayCode))
                            .filter((dayIndex) => dayIndex >= 0),
                    month: pattern.byMonthDate,
                    day: pattern.bySetPos,
                },
        }))}, [onModelChanged]);

    const onClearEndDateClicked = useCallback(() => onModelChanged?.((model) => ({
        ...model,
        endDate: undefined,
    })), [onModelChanged]);

    const targets = useMemo<ITokenizedInputItem[]>(
        () => (model.targets || []).map((target) => ({
            ...target,
            key: target.id,
            subText: target.info,
            imageUrl: target.image,
        })),
        [model.targets]);

    const onTargetsChanged = useCallback((items: ITokenizedInputItem[]) =>
        onModelChanged?.((model) => ({
            ...model,
            targets: items.map((item) => ({
                id: item.key,
                name: item.name,
                type: item.type,
                image: item.imageUrl,
            }))
        })), [onModelChanged]);

    const recurrenceOptions = useMemo(getRecurrenceOptions, []);

    const onRecurrenceOptionChanged = useCallback((
        _: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption<RecurrenceOptions>,
    ) => onModelChanged?.((model) => {
        if (!option.data || option.data === "NEVER") {
            return {
                ...model,
                recurrencePattern: { type: SpintrTypes.RecurrenceType.None, interval: 1 },
            };
        }

        let recurrencePattern: Spintr.RecurrencePattern;
        switch (option.data) {
        case "DAILY":
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Daily,
                interval: 1,
            };
            break;
        case "WEEKLY":
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Weekly,
                interval: 1,
                dayOfWeek: 1,
                daysOfWeek: [1],
            };
            break;
        case "MONTHLY":
            recurrencePattern = {
                type: SpintrTypes.RecurrenceType.Monthly,
                interval: 1,
                day: 1,
            };
            break;
        default:
            return model;
        }

        return {
            ...model,
            recurrencePattern,
        };
    }), [onModelChanged]);

    const selectedRecurrenceOption = useMemo(() => {
        switch (model.recurrencePattern?.type) {
        case SpintrTypes.RecurrenceType.Daily:
            return recurrenceOptions.find((opt) => opt.data === "DAILY");
        case SpintrTypes.RecurrenceType.Weekly:
            return recurrenceOptions.find((opt) => opt.data === "WEEKLY");
        case SpintrTypes.RecurrenceType.Monthly:
            return recurrenceOptions.find((opt) => opt.data === "MONTHLY");
        default:
            return recurrenceOptions.find((opt) => opt.data === "NEVER");
        }
    }, [model.recurrencePattern, recurrenceOptions]);

    const onRecurringChanged = useCallback<CheckboxChangeHandler>(
        (_, checked) => onModelChanged?.((model) => ({
            ...model,
            recurrencePattern: !checked
                ? { type: SpintrTypes.RecurrenceType.None, interval: 1 }
                : { type: SpintrTypes.RecurrenceType.Daily, interval: 1 },
        })),
        [onModelChanged]);

    const isRecurring = !!model?.recurrencePattern?.type
                     && model.recurrencePattern.type !== SpintrTypes.RecurrenceType.None;

    return (
        <div className="TaskListForm">
            <form action="#"
                  method="POST">
                <FormSection>
                    <FormControl>
                        <TextField aria-required={true}
                                   className="textField"
                                   label={l10n["Namn"]}
                                   name="name"
                                   onChange={onNameChanged}
                                   required={true}
                                   value={model.name} />
                    </FormControl>
                </FormSection>

                {/*
                <FormSection className="TaskListForm-dates">
                    <FormControl className="TaskListForm-startDate">
                        <DatePicker ariaLabel={l10n["Startdatum"]}
                                    firstDayOfWeek={1}
                                    isRequired={true}
                                    label={l10n["Startdatum"]}
                                    placeholder={l10n["Startdatum"]}
                                    value={model.startDate} />
                    </FormControl>
                    <FormControl className="TaskListForm-endDate">
                        <DatePicker ariaLabel={l10n["Slutdatum"]}
                                    className="TaskListForm-datePicker"
                                    firstDayOfWeek={1}
                                    isRequired={false}
                                    label={l10n["Slutdatum"]}
                                    placeholder={l10n["Slutdatum"]}
                                    value={model.endDate} />
                        <UnstyledButton className="clear-button"
                                        onClick={onClearEndDateClicked}>
                            <Visage2Icon color="dark-grey"
                                         icon="close-square"
                                         size="big" />
                        </UnstyledButton>
                    </FormControl>
                </FormSection>
                */}

                <FormSection title={l10n["Installningar"]}>
                    <FormControl>
                        <Checkbox label={l10n["ALLOW_CUSTOMIZATION"]}
                                  name="allowOverride"
                                  onChange={onAllowOverrideChanged}
                                  checked={model.allowOverride} />
                    </FormControl>

                    <FormControl>
                        <Checkbox label={l10n["RECURRING"]}
                                  name="recurring"
                                  onChange={onRecurringChanged}
                                  checked={isRecurring} />

                        <Conditional condition={isRecurring}>
                            <Dropdown label={l10n["QH_RESET_FREQUENCY"] + ":"}
                                      onChange={onRecurrenceOptionChanged}
                                      options={recurrenceOptions}
                                      selectedKey={selectedRecurrenceOption?.key} />
                        </Conditional>

                    </FormControl>
                </FormSection>

                {/*
                <FormSection>
                    <CalendarEventRecurringSelector
                        allowEmptyEndDate={true}
                        config={recurrenceConfig}
                        onChange={onRecurrencePatternChanged} />
                </FormSection>
                */}

                {/*
                <FormSection>
                    <FormControl>
                        <FormTokenizedObjectInput item={targets}
                                                  label={l10n["RiktadTillBeskrivning"]}
                                                  onChange={onTargetsChanged}
                                                  types={targetTypes} />
                    </FormControl>
                </FormSection>
                */}
            </form>
        </div>
    );
}

export default TaskListForm;

